import type { DateValue, RangeValue } from '@heroui/react';
import { EProductType, ESubSource } from '@quesmed/types/models';
import { ESubType } from '@quesmed/types/resolvers/query/admin';
import { create } from 'zustand';
import { EDateFilter, EProductCategory } from '@/config';
import type { OmitFunctions } from '@/types';
import { getDateRange } from '@/utils';
import { createSelectors } from './selector';

interface UniversityEarningsState {
  productIds: EProductType[];
  subType: ESubType;
  source: ESubSource;
  countryName: string;
  classYear: string;
  graduationYear: number;
  useNetEarning: boolean;
  productCategory: EProductCategory;
  dateFilter: EDateFilter;
  range: { from: Date | null | number; to: Date | null | number };
  dateRange: RangeValue<DateValue> | undefined;
  isFilterDrawerOpen: boolean;
  isLoading: boolean;

  setProductIds: (productIds: EProductType[]) => void;
  setSubType: (subType: ESubType) => void;
  setSource: (source: ESubSource) => void;
  setCountryName: (countryName: string) => void;
  setClassYear: (classYear: string) => void;
  setGraduationYear: (graduationYear: number) => void;
  setUseNetEarning: (useNetEarning: boolean) => void;
  setProductCategory: (productCategory: EProductCategory) => void;
  setDateFilter: (dateFilter: EDateFilter) => void;
  setIsFilterDrawerOpen: (isOpen: boolean) => void;
  setIsLoading: (isLoading: boolean) => void;
  setRange: (range: UniversityEarningsState['range']) => void;
  setDateRange: (dateRange: UniversityEarningsState['dateRange']) => void;
  reset: () => void;
}

const initialState: OmitFunctions<UniversityEarningsState> = {
  productIds: [EProductType.ALL],
  subType: ESubType.ALL,
  source: ESubSource.STRIPE,
  countryName: 'United Kingdom',
  classYear: '',
  graduationYear: -1,
  useNetEarning: true,
  productCategory: EProductCategory.ALL,
  dateFilter: EDateFilter.THIS_MONTH,
  range: getDateRange(EDateFilter.THIS_MONTH),
  dateRange: undefined,
  isFilterDrawerOpen: false,
  isLoading: false,
};

export const universityEarningsState = create<UniversityEarningsState>()((set) => ({
  ...initialState,
  setProductIds: (productIds) => set({ productIds }),
  setSubType: (subType) => set({ subType }),
  setSource: (source) => set({ source }),
  setCountryName: (countryName) => set({ countryName }),
  setClassYear: (classYear) => set({ classYear }),
  setGraduationYear: (graduationYear) => set({ graduationYear }),
  setUseNetEarning: (useNetEarning) => set({ useNetEarning }),
  setProductCategory: (productCategory) => set({ productCategory }),
  setDateFilter: (dateFilter) => set({ dateFilter }),
  setIsFilterDrawerOpen: (isOpen) => set({ isFilterDrawerOpen: isOpen }),
  setIsLoading: (isLoading) => set({ isLoading }),
  setRange: (range) => set({ range }),
  setDateRange: (dateRange) => set({ dateRange }),
  reset: () => set(initialState),
}));

export const useUniversityEarningsState = () => universityEarningsState((state) => state);
export const useUniversityEarnings = createSelectors(universityEarningsState);
